/**
 * Mock datasets for Report Preview + PDF metric id lists.
 * Intentional for UI and demos while the preview API is unavailable—do not delete
 * when endpoints ship; keep them as fixtures and as the shape reference for mappers.
 * Consumed by `ReportPreview.page.tsx`; `loadReportPreviewData` should eventually
 * return payloads that map onto these structures.
 */
export const PREVIEW_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Total Active Patients', value: '965' },
  { metric: 'Net New Patients', value: '+12' },
  { metric: 'Retention Rate', value: '71%' },
  { metric: 'Reactivation Rate', value: '14%' },
  {
    metric: 'At-Risk Patients (12+ months inactive)',
    value: '184 (23%)',
  },
]

/** PBH, Comparison mode — two value columns; matches Figma (Last Year / This Year for Year scope). */
export const PREVIEW_SUMMARY_COMPARISON_ROWS: {
  metric: string
  valueLeft: string
  valueRight: string
  emphasize?: boolean
}[] = [
  {
    metric: 'Total Active Patients',
    valueLeft: '965',
    valueRight: '965',
  },
  {
    metric: 'Net New Patients',
    valueLeft: '+12',
    valueRight: '+12',
  },
  {
    metric: 'Retention Rate',
    valueLeft: '71%',
    valueRight: '71%',
  },
  {
    metric: 'Reactivation Rate',
    valueLeft: '14%',
    valueRight: '14%',
  },
  {
    metric: 'At-Risk Patients (12+ months inactive)',
    valueLeft: '184 (23%)',
    valueRight: '184 (23%)',
    emphasize: true,
  },
]

export const PREVIEW_VISIT_ROWS: { recency: string; percentage: string }[] = [
  { recency: '0–3 Months', percentage: '43%' },
  { recency: '4–6 Months', percentage: '21%' },
  { recency: '7–9 Months', percentage: '10%' },
  { recency: '10–12 Months', percentage: '14%' },
  { recency: '12+ Months', percentage: '23%' },
]

export const PREVIEW_POPULATION_TREND_ROWS: {
  month: string
  active: string
  dormant: string
  lost: string
}[] = [
  { month: 'January', active: '120', dormant: '50', lost: '20' },
  { month: 'February', active: '200', dormant: '100', lost: '60' },
  { month: 'March', active: '180', dormant: '80', lost: '55' },
  { month: 'April', active: '190', dormant: '70', lost: '60' },
  { month: 'May', active: '320', dormant: '250', lost: '200' },
  { month: 'June', active: '650', dormant: '500', lost: '460' },
  { month: 'July', active: '670', dormant: '520', lost: '480' },
  { month: 'Augutst', active: '680', dormant: '540', lost: '490' },
  { month: 'September', active: '690', dormant: '560', lost: '500' },
  { month: 'October', active: '740', dormant: '600', lost: '520' },
  { month: 'November', active: '800', dormant: '640', lost: '540' },
  { month: 'December', active: '860', dormant: '680', lost: '560' },
]

export const PREVIEW_AGE_DISTRIBUTION_ROWS: {
  age: string
  percentage: string
}[] = [
  { age: '0-17', percentage: '38.6%' },
  { age: '18-34', percentage: '30.2%' },
  { age: '35-54', percentage: '18.8%' },
  { age: '55+', percentage: '12.4%' },
]

export const PREVIEW_PATIENT_VALUE_SUMMARY_ROWS: {
  metric: string
  value: string
  notes: string
}[] = [
  {
    metric: 'Average Patient Value (LTV)',
    value: '$1,136',
    notes: '+$16 vs last month',
  },
  {
    metric: 'Active Patient Revenue',
    value: '$632K',
    notes: '+2.7% vs last month',
  },
  {
    metric: 'AR Opportunity to schedule',
    value: '$180K',
    notes: '143 patients',
  },
  {
    metric: 'Unscheduled Treatment',
    value: '$87.6K',
    notes: '142 cases',
  },
  {
    metric: 'Total Outstanding AR',
    value: '$76.2K',
    notes: '72 accounts',
  },
]

export const PREVIEW_PROCEDURE_MIX_ROWS: {
  category: string
  revenue: string
  percentage: string
}[] = [
  { category: 'SRP', revenue: '$254,000', percentage: '32%' },
  { category: 'Crown & Bridge', revenue: '$197,000', percentage: '25%' },
  {
    category: 'Fillings & Restorations',
    revenue: '$150,000',
    percentage: '19%',
  },
  { category: 'Hygiene', revenue: '$104,000', percentage: '13%' },
  { category: 'Cash', revenue: '$80,000', percentage: '11%' },
]

export const PREVIEW_VALUE_SEGMENTS_ROWS: {
  range: string
  patientCount: string
  patientsPct: string
}[] = [
  { range: '$0 - $500', patientCount: '320', patientsPct: '33%' },
  { range: '$500 - $1,500', patientCount: '390', patientsPct: '40%' },
  { range: '$1,500 - $3,000', patientCount: '180', patientsPct: '19%' },
  { range: '$3,000+', patientCount: '75', patientsPct: '8%' },
]

export const PREVIEW_NEW_VS_EXISTING_ROWS: {
  category: string
  revenue: string
  patientCount: string
  avgRevenuePerPatient: string
}[] = [
  {
    category: 'New Patient',
    revenue: '$655,500',
    patientCount: '1,000',
    avgRevenuePerPatient: '$655.50',
  },
  {
    category: 'Existing Patient',
    revenue: '$124,500',
    patientCount: '250',
    avgRevenuePerPatient: '$498.00',
  },
]

export const PREVIEW_AR_OPPORTUNITY_ROWS: {
  provider: string
  patients: string
  unscheduledValue: string
  outstandingAr: string
}[] = [
  {
    provider: 'Dr. John Emerson',
    patients: '21',
    unscheduledValue: '$50,600.00',
    outstandingAr: '$20,600.00',
  },
  {
    provider: 'Dr. John Snow',
    patients: '15',
    unscheduledValue: '$16,200.00',
    outstandingAr: '$14,200.00',
  },
  {
    provider: 'Dr. Carla Smith',
    patients: '12',
    unscheduledValue: '$12,000.00',
    outstandingAr: '$9,900.00',
  },
  {
    provider: 'Dr. Dow John Jr.',
    patients: '26',
    unscheduledValue: '$37,900.00',
    outstandingAr: '$8,900.00',
  },
  {
    provider: 'Dr. Freddy John',
    patients: '18',
    unscheduledValue: '$21,500.00',
    outstandingAr: '$3,900.00',
  },
  {
    provider: 'Dr. Pete Johansen',
    patients: '72',
    unscheduledValue: '$76,200.00',
    outstandingAr: '$76,200.00',
  },
]

export const PREVIEW_HIGH_RISK_ROWS: {
  patientName: string
  lastVisitDaysAgo: string
  unscheduledTreatment: string
  noShowCount: string
  outstandingBalance: string
  riskScore: string
}[] = [
  {
    patientName: 'Carla Johnson Smith',
    lastVisitDaysAgo: '410',
    unscheduledTreatment: '$3,500.00',
    noShowCount: '3',
    outstandingBalance: '$720.00',
    riskScore: '91',
  },
  {
    patientName: 'Dow John Jr.',
    lastVisitDaysAgo: '365',
    unscheduledTreatment: '$1,200.00',
    noShowCount: '2',
    outstandingBalance: '$150.00',
    riskScore: '75',
  },
  {
    patientName: 'Joe Rocco',
    lastVisitDaysAgo: '380',
    unscheduledTreatment: '$4,200.00',
    noShowCount: '4',
    outstandingBalance: '$1,100.00',
    riskScore: '95',
  },
  {
    patientName: 'John Emerson',
    lastVisitDaysAgo: '320',
    unscheduledTreatment: '$2,400.00',
    noShowCount: '2',
    outstandingBalance: '$450.00',
    riskScore: '82',
  },
  {
    patientName: 'Pete Johansen',
    lastVisitDaysAgo: '340',
    unscheduledTreatment: '$2,600.00',
    noShowCount: '2',
    outstandingBalance: '$520.00',
    riskScore: '85',
  },
]

/** PBH, Comparison mode — two period columns per metric (mock: same as single-period for both). */
export const PREVIEW_VISIT_COMPARISON_ROWS: {
  recency: string
  percentageLeft: string
  percentageRight: string
}[] = PREVIEW_VISIT_ROWS.map((r) => ({
  recency: r.recency,
  percentageLeft: r.percentage,
  percentageRight: r.percentage,
}))

export const PREVIEW_POPULATION_TREND_COMPARISON_ROWS: {
  month: string
  activeLeft: string
  activeRight: string
  dormantLeft: string
  dormantRight: string
  lostLeft: string
  lostRight: string
}[] = PREVIEW_POPULATION_TREND_ROWS.map((r) => ({
  month: r.month,
  activeLeft: r.active,
  activeRight: r.active,
  dormantLeft: r.dormant,
  dormantRight: r.dormant,
  lostLeft: r.lost,
  lostRight: r.lost,
}))

export const PREVIEW_AGE_DISTRIBUTION_COMPARISON_ROWS: {
  age: string
  percentageLeft: string
  percentageRight: string
}[] = PREVIEW_AGE_DISTRIBUTION_ROWS.map((r) => ({
  age: r.age,
  percentageLeft: r.percentage,
  percentageRight: r.percentage,
}))

export const PREVIEW_HIGH_RISK_COMPARISON_ROWS: {
  patientName: string
  lastVisitDaysAgoLeft: string
  lastVisitDaysAgoRight: string
  unscheduledTreatmentLeft: string
  unscheduledTreatmentRight: string
  noShowCountLeft: string
  noShowCountRight: string
  outstandingBalanceLeft: string
  outstandingBalanceRight: string
  riskScoreLeft: string
  riskScoreRight: string
}[] = PREVIEW_HIGH_RISK_ROWS.map((r) => ({
  patientName: r.patientName,
  lastVisitDaysAgoLeft: r.lastVisitDaysAgo,
  lastVisitDaysAgoRight: r.lastVisitDaysAgo,
  unscheduledTreatmentLeft: r.unscheduledTreatment,
  unscheduledTreatmentRight: r.unscheduledTreatment,
  noShowCountLeft: r.noShowCount,
  noShowCountRight: r.noShowCount,
  outstandingBalanceLeft: r.outstandingBalance,
  outstandingBalanceRight: r.outstandingBalance,
  riskScoreLeft: r.riskScore,
  riskScoreRight: r.riskScore,
}))

export const PDF_COPY_METRICS: { id: string; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'lastVisitDistribution', label: 'Last Visit Distribution' },
  { id: 'patientPopulationTrend', label: 'Patient Population Trend' },
  { id: 'patientAgeDistribution', label: 'Patient Age Distribution' },
  { id: 'highRiskPatients', label: 'High Risk Patients' },
  { id: 'mediumRiskPatients', label: 'Medium Risk Patients' },
  { id: 'lowRiskPatients', label: 'Low Risk Patients' },
]

export const PDF_COPY_METRICS_PATIENT_VALUE: { id: string; label: string }[] = [
  { id: 'pvSummary', label: 'Summary' },
  {
    id: 'pvProductionMix',
    label: 'Production by Procedure Mix & Active Patient Value Segments',
  },
  {
    id: 'pvNewVsExisting',
    label: 'New vs Existing Production (Revenue Split)',
  },
  {
    id: 'pvArOpportunity',
    label: 'AR & Unscheduled Treatment Opportunity (By Provider)',
  },
]

export const ALL_METRICS_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const ALL_METRICS_PATIENT_VALUE_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_PATIENT_VALUE.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_RISK_COMPLIANCE_SUMMARY_ROWS: {
  metric: string
  value: string
  notes: string
}[] = [
  {
    metric: 'Patients w/ Unscheduled Treatment',
    value: '142 cases',
    notes: 'Sum of patients across unscheduled value bucket',
  },
  {
    metric: 'Recall Compliance Rate',
    value: '71%',
    notes: 'Completed recall visits / eligible recall patients',
  },
  {
    metric: 'Patients w/ Medical Alerts',
    value: '37 patients',
    notes: 'Distinct patients with >=1 active medical alert',
  },
  {
    metric: 'Patients Overdue (12+ months)',
    value: '184 patients',
    notes: 'Active base with last completed visit > 12 months',
  },
]

export const PREVIEW_RECALL_COMPLIANCE_TREND_ROWS: {
  month: string
  eligible: string
  completed: string
  rate: string
}[] = [
  { month: 'Jul', eligible: '520', completed: '343', rate: '66%' },
  { month: 'Aug', eligible: '535', completed: '358', rate: '67%' },
  { month: 'Sep', eligible: '548', completed: '378', rate: '69%' },
  { month: 'Oct', eligible: '560', completed: '392', rate: '70%' },
  { month: 'Nov', eligible: '572', completed: '406', rate: '71%' },
  { month: 'Dec', eligible: '570', completed: '405', rate: '71%' },
]

export const PREVIEW_UNSCHEDULED_BUCKET_ROWS: {
  bucket: string
  patients: string
  totalValue: string
}[] = [
  { bucket: '$0–$500', patients: '48', totalValue: '$14,400' },
  { bucket: '$500–$1,500', patients: '56', totalValue: '$56,000' },
  { bucket: '$1,500–$3,000', patients: '26', totalValue: '$52,000' },
  { bucket: '$3,000+', patients: '12', totalValue: '$45,600' },
]

export const PREVIEW_OVERDUE_GROUPING_ROWS: {
  bucket: string
  patients: string
  pctActive: string
}[] = [
  { bucket: '6–9 months', patients: '96', pctActive: '10%' },
  { bucket: '9–12 months', patients: '88', pctActive: '9%' },
  { bucket: '12–18 months', patients: '74', pctActive: '8%' },
  { bucket: '18–24 months', patients: '55', pctActive: '6%' },
  { bucket: '24+ months', patients: '31', pctActive: '3%' },
]

export const PREVIEW_MEDICAL_ALERTS_DETAIL_ROWS: {
  category: string
  patients: string
}[] = [
  { category: 'Allergy (med/latex)', patients: '12' },
  { category: 'Anticoagulants', patients: '7' },
  { category: 'Diabetes', patients: '6' },
  { category: 'Hypertension', patients: '5' },
  { category: 'Pregnancy', patients: '3' },
  { category: 'Other', patients: '4' },
]

export const PDF_COPY_METRICS_PATIENT_RISK: { id: string; label: string }[] = [
  { id: 'prSummary', label: 'Summary' },
  { id: 'prRecallTrend', label: 'Recall Compliance (Trend)' },
  { id: 'prUnscheduled', label: 'Patients with Unscheduled Treatment' },
  {
    id: 'prOverdue',
    label: 'Patients Overdue (Last Visit Grouping)',
  },
  { id: 'prMedicalAlerts', label: 'Patients with Medical Alerts' },
]

export const ALL_METRICS_PATIENT_RISK_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_PATIENT_RISK.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PDF_COPY_METRICS_FAMILY_OPPORTUNITY: {
  id: string
  label: string
}[] = [
  { id: 'foSummary', label: 'Summary' },
  { id: 'foBirthdays', label: 'Birthdays & Milestones (Next 30 Days)' },
  { id: 'foMultiFamily', label: 'Multi-family Scheduling Gap' },
  { id: 'foHouseholdGaps', label: 'Household Scheduling Gaps' },
]

export const PREVIEW_FAMILY_OPPORTUNITY_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Same-Family Opportunities', value: '46 patients' },
  { metric: 'Unscheduled Treatment', value: '18' },
  { metric: 'Households with Gaps', value: '32 families' },
  { metric: 'Overdue Recall', value: '14' },
  {
    metric: 'Upcoming Birthdays / Milestones (30 days)',
    value: '18 patients',
  },
  { metric: 'Unscheduled Family Member', value: '9' },
  { metric: 'Multi-family Scheduling Gaps', value: '24 gaps' },
  { metric: 'Hygiene Gap', value: '5' },
  { metric: 'Total', value: '46' },
]

export const PREVIEW_FAMILY_OPPORTUNITY_BIRTHDAY_ROWS: {
  patient: string
  date: string
  household: string
  actions: string
}[] = [
  {
    patient: 'John Emerson',
    date: '48',
    household: 'Brown',
    actions: 'Recall Text',
  },
  {
    patient: 'John Snow',
    date: '56',
    household: 'Patel',
    actions: 'Offer family block',
  },
  {
    patient: 'Carla Johnson Smith',
    date: '26',
    household: 'Chen',
    actions: 'Hygiene reminder',
  },
  {
    patient: 'Dow John Jr.',
    date: '12',
    household: 'Garcia',
    actions: 'Tx follow-up',
  },
  {
    patient: 'Freddy John Doe',
    date: '12',
    household: 'Lopez',
    actions: 'Schedule together',
  },
]

export const PREVIEW_FAMILY_OPPORTUNITY_MULTI_GAP_ROWS: {
  gapBucket: string
  households: string
}[] = [
  { gapBucket: '0–7 days', households: '6' },
  { gapBucket: '8–14 days', households: '10' },
  { gapBucket: '15–30 days', households: '5' },
  { gapBucket: '31–60 days', households: '2' },
  { gapBucket: '60+ days', households: '1' },
]

export const PREVIEW_FAMILY_OPPORTUNITY_HOUSEHOLD_GAP_ROWS: {
  household: string
  members: string
  dueUnscheduled: string
  recommendedAction: string
}[] = [
  {
    household: 'Brown',
    members: '96',
    dueUnscheduled: '10%',
    recommendedAction: 'Offer a 60-min family block next week',
  },
  {
    household: 'Patel',
    members: '88',
    dueUnscheduled: '9%',
    recommendedAction: 'Add to same-day openings',
  },
  {
    household: 'Chen',
    members: '74',
    dueUnscheduled: '8%',
    recommendedAction: 'Reserve weekend block options',
  },
  {
    household: 'Garcia',
    members: '55',
    dueUnscheduled: '6%',
    recommendedAction: 'Fill hygiene slot within 7 days',
  },
  {
    household: 'Lopez',
    members: '31',
    dueUnscheduled: '3%',
    recommendedAction: 'Recall call + family scheduling offer',
  },
]

export const ALL_METRICS_FAMILY_OPPORTUNITY_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_FAMILY_OPPORTUNITY.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_RECALL_REACTIVATION_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Recall Compliance Rate', value: '71' },
  { metric: 'Reactivation Rate', value: '14%' },
  { metric: 'Patients Due for Recall', value: '312' },
  { metric: 'Hygiene Overdue (12+ months)', value: '184' },
]

export const PREVIEW_RECALL_REACTIVATION_COMPLIANCE_ROWS: {
  month: string
  eligible: string
  completed: string
  rate: string
}[] = [
  { month: 'July', eligible: '520', completed: '343', rate: '66%' },
  { month: 'August', eligible: '535', completed: '358', rate: '67%' },
  { month: 'September', eligible: '548', completed: '357', rate: '69%' },
  { month: 'October', eligible: '560', completed: '392', rate: '70%' },
  { month: 'November', eligible: '572', completed: '406', rate: '71%' },
  { month: 'December', eligible: '570', completed: '405', rate: '71%' },
]

export const PREVIEW_RECALL_REACTIVATION_PROVIDER_ROWS: {
  provider: string
  eligible: string
  completed: string
  responseRate: string
}[] = [
  {
    provider: 'Dr. Emerson',
    eligible: '68',
    completed: '49',
    responseRate: '72%',
  },
  {
    provider: 'Dr. Snow',
    eligible: '54',
    completed: '37',
    responseRate: '69%',
  },
  {
    provider: 'Dr. Smith',
    eligible: '62',
    completed: '46',
    responseRate: '74%',
  },
  {
    provider: 'Dr. Patel',
    eligible: '71',
    completed: '49',
    responseRate: '69%',
  },
  {
    provider: 'Dr. Garcia',
    eligible: '57',
    completed: '41',
    responseRate: '72%',
  },
]

export const PREVIEW_REACTIVATION_TREND_ROWS: {
  month: string
  dormantPool: string
  reactivatedPatients: string
  reactivationRate: string
}[] = [
  {
    month: 'July',
    dormantPool: '820',
    reactivatedPatients: '90',
    reactivationRate: '11%',
  },
  {
    month: 'August',
    dormantPool: '815',
    reactivatedPatients: '98',
    reactivationRate: '12%',
  },
  {
    month: 'September',
    dormantPool: '810',
    reactivatedPatients: '105',
    reactivationRate: '13%',
  },
  {
    month: 'October',
    dormantPool: '805',
    reactivatedPatients: '112',
    reactivationRate: '14%',
  },
  {
    month: 'November',
    dormantPool: '800',
    reactivatedPatients: '110',
    reactivationRate: '14%',
  },
  {
    month: 'December',
    dormantPool: '798',
    reactivatedPatients: '112',
    reactivationRate: '14%',
  },
]

export const PREVIEW_HYGIENE_OVERDUE_ACTION_ROWS: {
  patient: string
  lastHygiene: string
  daysSince: string
  phone: string
}[] = [
  {
    patient: 'John Smith',
    lastHygiene: 'Apr 18',
    daysSince: '320',
    phone: '(555) 0101',
  },
  {
    patient: 'Maria Lopez',
    lastHygiene: 'May 02',
    daysSince: '275',
    phone: '(555) 0108',
  },
  {
    patient: 'David Chen',
    lastHygiene: 'Mar 10',
    daysSince: '410',
    phone: '(555) 0122',
  },
  {
    patient: 'Emily Johnson',
    lastHygiene: 'Apr 03',
    daysSince: '365',
    phone: '(555) 0114',
  },
  {
    patient: 'Ahmed Khan',
    lastHygiene: 'May 17',
    daysSince: '290',
    phone: '(555) 0130',
  },
]

export const PDF_COPY_METRICS_RECALL_REACTIVATION: {
  id: string
  label: string
}[] = [
  { id: 'rrSummary', label: 'Summary' },
  { id: 'rrRecallTrend', label: 'Recall Compliance (Trend)' },
  { id: 'rrProvider', label: 'Provider-specific Recall Response' },
  { id: 'rrReactivationTrend', label: 'Reactivation Rate (Trend)' },
  { id: 'rrHygieneOverdue', label: 'Hygiene Overdue Patients (Action List)' },
]

export const ALL_METRICS_RECALL_REACTIVATION_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_RECALL_REACTIVATION.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_SCHEDULE_RELIABILITY_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'No-show Rate', value: '4.8%' },
  { metric: 'Cancellation Rate', value: '9.6%' },
  { metric: 'No-shows (count)', value: '58%' },
  { metric: 'Repeat Offenders', value: '23 patients' },
]

export const PREVIEW_SCHEDULE_RELIABILITY_TREND_ROWS: {
  month: string
  scheduledAppointments: string
  noShows: string
  noShowRate: string
  cancellations: string
  cancelRate: string
}[] = [
  {
    month: 'July',
    scheduledAppointments: '1180',
    noShows: '61',
    noShowRate: '5.2%',
    cancellations: '106',
    cancelRate: '9.0%',
  },
  {
    month: 'August',
    scheduledAppointments: '1205',
    noShows: '60',
    noShowRate: '5.0%',
    cancellations: '111',
    cancelRate: '9.2%',
  },
  {
    month: 'September',
    scheduledAppointments: '1220',
    noShows: '60',
    noShowRate: '4.9%',
    cancellations: '115',
    cancelRate: '9.4%',
  },
  {
    month: 'October',
    scheduledAppointments: '1235',
    noShows: '59',
    noShowRate: '4.8%',
    cancellations: '117',
    cancelRate: '9.5%',
  },
  {
    month: 'November',
    scheduledAppointments: '1250',
    noShows: '59',
    noShowRate: '4.7%',
    cancellations: '120',
    cancelRate: '9.6%',
  },
  {
    month: 'December',
    scheduledAppointments: '1210',
    noShows: '58',
    noShowRate: '4.8%',
    cancellations: '116',
    cancelRate: '9.6%',
  },
]

export const PREVIEW_SCHEDULE_RELIABILITY_NO_SHOW_PATIENT_ROWS: {
  patient: string
  date: string
  provider: string
  procedure: string
  phone: string
}[] = [
  {
    patient: 'John Smith',
    date: 'Dec 14',
    provider: 'Dr. Smith',
    procedure: 'Hygiene',
    phone: '(555) 0101',
  },
  {
    patient: 'Sophia Chen',
    date: 'Dec 12',
    provider: 'Dr. Patel',
    procedure: 'Exam',
    phone: '(555) 0112',
  },
  {
    patient: 'Ethan Patel',
    date: 'Dec 10',
    provider: 'Dr. Emerson',
    procedure: 'Fillings',
    phone: '(555) 0133',
  },
  {
    patient: 'Noah Garcia',
    date: 'Dec 08',
    provider: 'Dr. Snow',
    procedure: 'Hygiene',
    phone: '(555) 0144',
  },
  {
    patient: 'Mia Lopez',
    date: 'Dec 06',
    provider: 'Dr. Smith',
    procedure: 'SRP',
    phone: '(555) 0155',
  },
]

export const PREVIEW_SCHEDULE_RELIABILITY_REPEAT_OFFENDER_ROWS: {
  patient: string
  noShows: string
  cancellations: string
  lastEvent: string
}[] = [
  {
    patient: 'John Emerson',
    noShows: '2',
    cancellations: '1',
    lastEvent: 'Dec 12',
  },
  {
    patient: 'John Snow',
    noShows: '1',
    cancellations: '2',
    lastEvent: 'Dec 05',
  },
  {
    patient: 'Carla Johnson Smith',
    noShows: '2',
    cancellations: '0',
    lastEvent: 'Nov 28',
  },
  {
    patient: 'Dow John Jr.',
    noShows: '1',
    cancellations: '2',
    lastEvent: 'Nov 10',
  },
  {
    patient: 'Freddy John Doe',
    noShows: '2',
    cancellations: '1',
    lastEvent: 'Nov 03',
  },
]

export const PREVIEW_SCHEDULE_RELIABILITY_CANCELLATION_PATTERN_ROWS: {
  window: string
  cancellations: string
  share: string
}[] = [
  { window: 'Less than 24 hrs', cancellations: '38', share: '41%' },
  { window: '24-48 hrs', cancellations: '24', share: '26%' },
  { window: '2-7 days', cancellations: '21', share: '23%' },
  { window: '7+ days', cancellations: '9', share: '10%' },
]

export const PDF_COPY_METRICS_SCHEDULE_RELIABILITY: {
  id: string
  label: string
}[] = [
  { id: 'srSummary', label: 'Summary' },
  { id: 'srRateTrend', label: 'No-show & Cancellation Rate (Trend)' },
  { id: 'srNoShowList', label: 'No-show Patient List (Most Recent)' },
  {
    id: 'srRepeatOffenders',
    label: 'Repeat Offenders (2+ No-shows or Cancellations)',
  },
  { id: 'srCancellationPattern', label: 'Cancellation Patterns (Window)' },
]

export const ALL_METRICS_SCHEDULE_RELIABILITY_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_SCHEDULE_RELIABILITY.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PDF_COPY_METRICS_TREATMENT_CONVERSION: {
  id: string
  label: string
}[] = [
  {
    id: 'tcfSummary',
    label: 'Summary (Conversion Funnel & KPIs)',
  },
  {
    id: 'tcfOpportunities',
    label: 'Unscheduled Treatment Opportunities',
  },
  {
    id: 'tcfProvider',
    label: 'Exam-based Unscheduled Treatment (by Provider)',
  },
  {
    id: 'tcfMonthly',
    label: 'Completed vs Diagnosed (Monthly)',
  },
]

export const ALL_METRICS_TREATMENT_CONVERSION_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_TREATMENT_CONVERSION.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PDF_COPY_METRICS_REFERRAL_REPUTATION: {
  id: string
  label: string
}[] = [
  { id: 'refSummary', label: 'Summary (KPIs & Referrals Monthly)' },
  {
    id: 'rrRevenueMonthly',
    label: 'Revenue from Referred Patients (Monthly)',
  },
  { id: 'rrNpsTrend', label: 'NPS / Feedback Trend (Monthly)' },
  { id: 'rrFeedbackChannels', label: 'Feedback Channels (Period)' },
]

export const ALL_METRICS_REFERRAL_REPUTATION_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_REFERRAL_REPUTATION.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PDF_COPY_METRICS_MARKETING_CAMPAIGN: {
  id: string
  label: string
}[] = [
  { id: 'mcpSummary', label: 'Summary (KPIs)' },
  {
    id: 'mcpMonthlyTrend',
    label: 'Monthly Trend (Spend, New Patients, Revenue)',
  },
  { id: 'mcpConversionFunnel', label: 'Conversion Funnel (All Channels)' },
  { id: 'mcpChannelPerformance', label: 'Channel Performance' },
]

export const ALL_METRICS_MARKETING_CAMPAIGN_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_MARKETING_CAMPAIGN.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_REFERRAL_SUMMARY_KPI_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Patient Referrals Made', value: '112' },
  { metric: 'Revenue from Referred Patients', value: '$86,000.00' },
  { metric: 'NPS (Average)', value: '54' },
  { metric: 'Feedback Response Rate', value: '18%' },
]

export const PREVIEW_REFERRAL_MONTHLY_REFERRALS_ROWS: {
  month: string
  referrals: string
}[] = [
  { month: 'Jul', referrals: '15' },
  { month: 'Aug', referrals: '16' },
  { month: 'Sep', referrals: '18' },
  { month: 'Oct', referrals: '19' },
  { month: 'Nov', referrals: '21' },
  { month: 'Dec', referrals: '23' },
]

export const PREVIEW_REFERRAL_REVENUE_MONTHLY_ROWS: {
  month: string
  revenue: string
}[] = [
  { month: 'Jul', revenue: '$12,000.00' },
  { month: 'Aug', revenue: '$13,000.00' },
  { month: 'Sep', revenue: '$14,000.00' },
  { month: 'Oct', revenue: '$15,000.00' },
  { month: 'Nov', revenue: '$16,000.00' },
  { month: 'Dec', revenue: '$16,000.00' },
]

export const PREVIEW_REFERRAL_NPS_TREND_ROWS: {
  month: string
  nps: string
  responses: string
}[] = [
  { month: 'Jul', nps: '48', responses: '34' },
  { month: 'Aug', nps: '49', responses: '38' },
  { month: 'Sep', nps: '50', responses: '40' },
  { month: 'Oct', nps: '52', responses: '41' },
  { month: 'Nov', nps: '53', responses: '42' },
  { month: 'Dec', nps: '54', responses: '43' },
]

export const PREVIEW_REFERRAL_FEEDBACK_CHANNEL_ROWS: {
  id: string
  channel: string
  responses: string
  share: string
  avgRating: string
}[] = [
  {
    id: 'sms',
    channel: 'SMS survey',
    responses: '124',
    share: '52%',
    avgRating: '4.6',
  },
  {
    id: 'email',
    channel: 'Email survey',
    responses: '62',
    share: '26%',
    avgRating: '4.5',
  },
  {
    id: 'google',
    channel: 'Google reviews',
    responses: '38',
    share: '16%',
    avgRating: '4.7',
  },
  {
    id: 'others',
    channel: 'Others',
    responses: '14',
    share: '6%',
    avgRating: '4.4',
  },
]

export const PREVIEW_MCP_SUMMARY_KPI_ROWS: { metric: string; value: string }[] =
  [
    { metric: 'Campaign ROI', value: '472.7%' },
    { metric: 'Cost per acquired patient (CPA)', value: '$178.00' },
    { metric: 'LTV-to-CAC ratio', value: '9.26x' },
    { metric: 'Lead-to-new conversion rate', value: '26.5%' },
  ]

export const PREVIEW_MCP_MONTHLY_TREND_ROWS: {
  month: string
  spend: string
  newPatients: string
  revenue: string
}[] = [
  {
    month: 'Jul',
    spend: '$3,100.00',
    newPatients: '15',
    revenue: '$14,000.00',
  },
  {
    month: 'Aug',
    spend: '$3,400.00',
    newPatients: '18',
    revenue: '$17,000.00',
  },
  {
    month: 'Sep',
    spend: '$3,600.00',
    newPatients: '19',
    revenue: '$19,000.00',
  },
  {
    month: 'Oct',
    spend: '$4,100.00',
    newPatients: '23',
    revenue: '$22,000.00',
  },
  {
    month: 'Nov',
    spend: '$3,950.00',
    newPatients: '24',
    revenue: '$26,000.00',
  },
  {
    month: 'Dec',
    spend: '$4,250.00',
    newPatients: '28',
    revenue: '$30,000.00',
  },
]

export const PREVIEW_MCP_FUNNEL_ROWS: {
  stage: string
  count: string
  rateVsPrior: string
}[] = [
  { stage: 'Leads', count: '480', rateVsPrior: '-' },
  { stage: 'Appointments', count: '191', rateVsPrior: '39.8% of leads' },
  { stage: 'New Patients', count: '127', rateVsPrior: '66.5% of appts' },
]

export const PREVIEW_MCP_CHANNEL_PERFORMANCE_ROWS: {
  id: string
  channel: string
  spend: string
  leads: string
  appointments: string
  newPatients: string
  revenue90d: string
  cpa: string
  roi: string
  leadToNew: string
}[] = [
  {
    id: 'google',
    channel: 'Google Ads',
    spend: '$12,000.00',
    leads: '180',
    appointments: '72',
    newPatients: '54',
    revenue90d: '$64,000.00',
    cpa: '$222.00',
    roi: '433.3%',
    leadToNew: '30.0%',
  },
  {
    id: 'meta',
    channel: 'Meta Ads',
    spend: '$9,000.00',
    leads: '140',
    appointments: '55',
    newPatients: '41',
    revenue90d: '$43,000.00',
    cpa: '$220.00',
    roi: '377.8%',
    leadToNew: '29.3%',
  },
  {
    id: 'email',
    channel: 'Email',
    spend: '$900.00',
    leads: '90',
    appointments: '36',
    newPatients: '18',
    revenue90d: '$12,000.00',
    cpa: '$50.00',
    roi: '1,233.3%',
    leadToNew: '20%',
  },
  {
    id: 'sms',
    channel: 'SMS',
    spend: '$450.00',
    leads: '70',
    appointments: '28',
    newPatients: '14',
    revenue90d: '$9,000.00',
    cpa: '$32.00',
    roi: '1,900.0%',
    leadToNew: '20%',
  },
]

export const PDF_COPY_METRICS_COMMUNICATION_CENTER: {
  id: string
  label: string
}[] = [
  { id: 'ccCallsKpiSummary', label: 'Calls KPI Summary' },
  { id: 'ccMessagingKpiSummary', label: 'Messaging KPI Summary' },
  { id: 'ccEffectiveness', label: 'Communication Effectiveness' },
  { id: 'ccCallsVolume', label: 'Calls Volume & Outcomes' },
  { id: 'ccMessaging', label: 'Messaging Performance' },
]

export const ALL_METRICS_COMMUNICATION_CENTER_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_COMMUNICATION_CENTER.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_CC_CALLS_KPI_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Total calls', value: '526' },
  { metric: 'Answered calls', value: '456' },
  { metric: 'Answer rate', value: '87%' },
  { metric: 'Missed calls', value: '3.8%' },
  { metric: 'Declined calls', value: '15' },
  { metric: 'Follow-ups', value: '20' },
  { metric: 'Avg call time', value: '3m 42s' },
  { metric: 'Positive sentiment share', value: '75%' },
]

export const PREVIEW_CC_MESSAGING_KPI_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Inbound messages', value: '840' },
  { metric: 'Replied messages', value: '780' },
  { metric: 'Response rate', value: '92.9%' },
  { metric: 'Open/unreplied', value: '46' },
  { metric: 'Median first response time', value: '7 min' },
  { metric: 'Average first response time', value: '11.5 min' },
  { metric: 'SLA breach rate', value: '4%' },
]

export const PREVIEW_CC_CALLS_VOLUME_ROWS: {
  period: string
  total: string
  answered: string
  missed: string
  declined: string
  followUps: string
}[] = [
  {
    period: 'Mon',
    total: '78',
    answered: '68',
    missed: '3',
    declined: '2',
    followUps: '3',
  },
  {
    period: 'Tue',
    total: '70',
    answered: '62',
    missed: '2',
    declined: '2',
    followUps: '2',
  },
  {
    period: 'Wed',
    total: '82',
    answered: '73',
    missed: '3',
    declined: '2',
    followUps: '2',
  },
  {
    period: 'Thu',
    total: '71',
    answered: '60',
    missed: '3',
    declined: '2',
    followUps: '3',
  },
  {
    period: 'Fri',
    total: '90',
    answered: '81',
    missed: '4',
    declined: '3',
    followUps: '3',
  },
  {
    period: 'Sat',
    total: '66',
    answered: '56',
    missed: '2',
    declined: '2',
    followUps: '3',
  },
  {
    period: 'Sun',
    total: '69',
    answered: '56',
    missed: '3',
    declined: '2',
    followUps: '4',
  },
]

export const PREVIEW_CC_MESSAGING_ROWS: {
  day: string
  inbound: string
  replied: string
  unreplied: string
}[] = [
  { day: 'Mon', inbound: '120', replied: '112', unreplied: '8' },
  { day: 'Tue', inbound: '110', replied: '101', unreplied: '9' },
  { day: 'Wed', inbound: '135', replied: '126', unreplied: '9' },
  { day: 'Thu', inbound: '105', replied: '98', unreplied: '7' },
  { day: 'Fri', inbound: '150', replied: '142', unreplied: '8' },
  { day: 'Sat', inbound: '95', replied: '90', unreplied: '5' },
  { day: 'Sun', inbound: '125', replied: '111', unreplied: '14' },
]

export const PREVIEW_CC_EFFECTIVENESS_ROWS: {
  metric: string
  rate: string
}[] = [
  { metric: 'SMS Open Rate', rate: '81.13%' },
  { metric: 'SMS Response Rate', rate: '15.28%' },
  { metric: 'Email Open Rate', rate: '45.50%' },
  { metric: 'Email CTR', rate: '11.25%' },
  { metric: 'Recall Campaign Success', rate: '23.91%' },
  { metric: 'Reactivation Success Rate', rate: '14.76%' },
  { metric: 'Reminder Confirmation Rate', rate: '75%' },
]

export const PREVIEW_SA_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Patients Seen', value: '526' },
  { metric: 'Avg Filled Slots / Day', value: '456' },
  { metric: 'Avg Open Slots / Day', value: '20' },
  { metric: 'Avg Hygiene Gaps / Day', value: '3.8%' },
  { metric: 'Scheduled Production (90d)', value: '15' },
  { metric: 'Production Target (90d)', value: '20' },
  { metric: 'Total No Shows (90d)', value: '3m 42s' },
]

export const PREVIEW_SA_NO_SHOWS_ROWS: { month: string; noShows: string }[] = [
  { month: 'Dec', noShows: '91' },
  { month: 'Jan', noShows: '112' },
  { month: 'Feb', noShows: '124' },
  { month: 'Mar', noShows: '22' },
]

export const PREVIEW_SA_FILLED_OPEN_ROWS: {
  month: string
  averageFilled: string
  averageOpen: string
}[] = [
  { month: 'Dec', averageFilled: '77.9', averageOpen: '18.9' },
  { month: 'Jan', averageFilled: '77.2', averageOpen: '16.7' },
  { month: 'Feb', averageFilled: '78.0', averageOpen: '17.9' },
  { month: 'Mar', averageFilled: '81.0', averageOpen: '18.6' },
]

export const PREVIEW_SA_HYGIENE_GAPS_ROWS: {
  month: string
  averageGaps: string
}[] = [
  { month: 'Dec', averageGaps: '5.7' },
  { month: 'Jan', averageGaps: '6.2' },
  { month: 'Feb', averageGaps: '6.0' },
  { month: 'Mar', averageGaps: '6.0' },
]

export const PREVIEW_SA_PRODUCTION_ROWS: {
  month: string
  scheduledProduction: string
  targetProduction: string
}[] = [
  {
    month: 'Dec',
    scheduledProduction: '$976,041.00',
    targetProduction: '$1,092,000.00',
  },
  {
    month: 'Jan',
    scheduledProduction: '$1,174,209.00',
    targetProduction: '$1,302,000.00',
  },
  {
    month: 'Feb',
    scheduledProduction: '$1,058,197.00',
    targetProduction: '$1,176,000.00',
  },
  {
    month: 'Mar',
    scheduledProduction: '$179,014.00',
    targetProduction: '$210,000.00',
  },
]

export const PDF_COPY_METRICS_SCHEDULED_APPOINTMENTS: {
  id: string
  label: string
}[] = [
  { id: 'saSummary', label: 'Summary' },
  { id: 'saNoShows', label: 'No-shows' },
  { id: 'saFilledOpen', label: 'Average Filled vs Open Slots' },
  { id: 'saHygieneGaps', label: 'Average Hygiene Gaps' },
  {
    id: 'saProductionTargetScheduled',
    label: 'Production Target vs Scheduled',
  },
]

export const ALL_METRICS_SCHEDULED_APPOINTMENTS_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_SCHEDULED_APPOINTMENTS.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_CRU_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Chair Time Used', value: '11,223 min' },
  { metric: 'Chair Time Available', value: '13,920 min' },
  { metric: 'Avg. Rooms Used', value: '4.5' },
  { metric: 'Avg. Room Operations', value: '4.1' },
  { metric: 'Utilization Rate', value: '80.6%' },
]

export const PREVIEW_CRU_CHAIR_TIME_ROWS: {
  month: string
  chairTimeUsed: string
  chairTimeAvailable: string
}[] = [
  { month: 'Dec', chairTimeUsed: '2,670', chairTimeAvailable: '3,520' },
  { month: 'Jan', chairTimeUsed: '3,062', chairTimeAvailable: '3,620' },
  { month: 'Feb', chairTimeUsed: '3,003', chairTimeAvailable: '3,320' },
  { month: 'Mar', chairTimeUsed: '2,488', chairTimeAvailable: '3,520' },
]

export const PREVIEW_CRU_PROVIDERS_WORKING_ROWS: {
  month: string
  providersWorking: string
}[] = [
  { month: 'Dec', providersWorking: '2.9' },
  { month: 'Jan', providersWorking: '3.3' },
  { month: 'Feb', providersWorking: '3.0' },
  { month: 'Mar', providersWorking: '3.2' },
]

export const PREVIEW_CRU_OPERATIONS_EFFICIENCY_ROWS: {
  month: string
  efficiency: string
}[] = [
  { month: 'Dec', efficiency: '75.9%' },
  { month: 'Jan', efficiency: '66.9%' },
  { month: 'Feb', efficiency: '85.33%' },
  { month: 'Mar', efficiency: '81.5%' },
]

export const PDF_COPY_METRICS_CHAIR_ROOM_UTILIZATION: {
  id: string
  label: string
}[] = [
  { id: 'cruSummary', label: 'Summary' },
  { id: 'cruChairTime', label: 'Chair Time Used vs Available' },
  { id: 'cruProvidersWorking', label: 'Providers Working' },
  { id: 'cruOperationsEfficiency', label: 'Operations Efficiency' },
]

export const ALL_METRICS_CHAIR_ROOM_UTILIZATION_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_CHAIR_ROOM_UTILIZATION.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_HP_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Hygiene Visits (Completed)', value: '2,369' },
  { metric: 'Hygiene Openings (Total)', value: '511' },
  { metric: 'Hygiene Utilization %', value: '82.3%' },
  // TODO: unhide Recall Compliance and Hygiene Reactivation when ready for implementation
  // { metric: 'Recall Compliance %', value: '67.5%' },
  // { metric: 'Hygiene Reactivation (Returned)', value: '502' },
]

export const PREVIEW_HP_OPENINGS_ROWS: {
  month: string
  avgOpenings: string
}[] = [
  { month: 'Dec', avgOpenings: '6.3' },
  { month: 'Jan', avgOpenings: '5.6' },
  { month: 'Feb', avgOpenings: '5.2' },
  { month: 'Mar', avgOpenings: '5.6' },
]

export const PREVIEW_HP_UTILIZATION_ROWS: {
  month: string
  utilization: string
}[] = [
  { month: 'Dec', utilization: '80.4' },
  { month: 'Jan', utilization: '82.4' },
  { month: 'Feb', utilization: '83.8' },
  { month: 'Mar', utilization: '82.5' },
]

export const PREVIEW_HP_RECALL_COMPLIANCE_ROWS: {
  month: string
  compliance: string
}[] = [
  { month: 'Dec', compliance: '69.2' },
  { month: 'Jan', compliance: '66.6' },
  { month: 'Feb', compliance: '66.7' },
  { month: 'Mar', compliance: '68.2' },
]

export const PREVIEW_HP_REACTIVATION_ROWS: {
  month: string
  reactivation: string
}[] = [
  { month: 'Dec', reactivation: '18.2' },
  { month: 'Jan', reactivation: '20.5' },
  { month: 'Feb', reactivation: '19.3' },
  { month: 'Mar', reactivation: '18.5' },
]

export const PDF_COPY_METRICS_HYGIENE_PERFORMANCE: {
  id: string
  label: string
}[] = [
  { id: 'hpSummary', label: 'Summary' },
  { id: 'hpOpenings', label: 'Hygiene Openings (Avg per Day) - Monthly' },
  { id: 'hpUtilization', label: 'Hygiene Utilization % - Monthly' },
  // TODO: unhide Recall Compliance and Hygiene Reactivation when ready for implementation
  // { id: 'hpRecallCompliance', label: 'Recall Compliance % - Monthly' },
  // {
  //   id: 'hpReactivationSuccess',
  //   label: 'Hygiene Reactivation Success % - Monthly',
  // },
]

export const ALL_METRICS_HYGIENE_PERFORMANCE_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_HYGIENE_PERFORMANCE.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_OBT_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Lab Follow-ups', value: '515' },
  { metric: 'Overdue Claims', value: '816' },
  { metric: 'Daily Tasks Due', value: '1,200' },
  { metric: 'Expected AR', value: '$368,850.00' },
]

export const PREVIEW_OBT_LAB_FOLLOW_UPS_ROWS: {
  month: string
  labFollowUps: string
}[] = [
  { month: 'Dec', labFollowUps: '135' },
  { month: 'Jan', labFollowUps: '183' },
  { month: 'Feb', labFollowUps: '168' },
  { month: 'Mar', labFollowUps: '29' },
]

export const PREVIEW_OBT_OVERDUE_CLAIMS_ROWS: {
  month: string
  overdueClaims: string
}[] = [
  { month: 'Dec', overdueClaims: '256' },
  { month: 'Jan', overdueClaims: '278' },
  { month: 'Feb', overdueClaims: '217' },
  { month: 'Mar', overdueClaims: '65' },
]

export const PREVIEW_OBT_DAILY_TASKS_ROWS: {
  month: string
  tasksDue: string
}[] = [
  { month: 'Dec', tasksDue: '329' },
  { month: 'Jan', tasksDue: '431' },
  { month: 'Feb', tasksDue: '378' },
  { month: 'Mar', tasksDue: '62' },
]

export const PREVIEW_OBT_EXPECTED_AR_ROWS: {
  month: string
  expectedAr: string
}[] = [
  { month: 'Dec', expectedAr: '$103,092.00' },
  { month: 'Jan', expectedAr: '$125,010.00' },
  { month: 'Feb', expectedAr: '$123,398.00' },
  { month: 'Mar', expectedAr: '$17,350.00' },
]

export const PDF_COPY_METRICS_OPERATIONAL_BACKLOG_TASKS: {
  id: string
  label: string
}[] = [
  { id: 'obtSummary', label: 'Summary' },
  { id: 'obtLabFollowUps', label: 'Lab Follow-ups' },
  { id: 'obtOverdueClaims', label: 'Overdue Claims' },
  { id: 'obtDailyTasks', label: 'Daily Tasks Due' },
  { id: 'obtExpectedAr', label: 'Expected AR Today' },
]

export const ALL_METRICS_OPERATIONAL_BACKLOG_TASKS_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_OPERATIONAL_BACKLOG_TASKS.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_PP_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Production', value: '$818,717.00' },
  { metric: 'Production Goal', value: '$855,188.00' },
  { metric: 'Avg Production per Hour', value: '$1,236.00' },
  { metric: 'Avg Production per Day', value: '$9,097.00' },
]

export const PREVIEW_PP_PRODUCTION_ROWS: {
  month: string
  production: string
}[] = [
  { month: 'Dec', production: '$233,383.00' },
  { month: 'Jan', production: '$288,297.00' },
  { month: 'Feb', production: '$251,822.00' },
  { month: 'Mar', production: '$45,215.00' },
]

export const PREVIEW_PP_PRODUCTION_GOAL_ROWS: {
  month: string
  goal: string
}[] = [
  { month: 'Dec', goal: '$243,169.00' },
  { month: 'Jan', goal: '$298,716.00' },
  { month: 'Feb', goal: '$263,705.00' },
  { month: 'Mar', goal: '$49,598.00' },
]

export const PREVIEW_PP_PER_HOUR_ROWS: {
  month: string
  perHour: string
}[] = [
  { month: 'Dec', perHour: '$1,222.11' },
  { month: 'Jan', perHour: '$1,250.29' },
  { month: 'Feb', perHour: '$1,219.18' },
  { month: 'Mar', perHour: '$1,316.26' },
]

export const PREVIEW_PP_PER_DAY_ROWS: {
  month: string
  perDay: string
}[] = [
  { month: 'Dec', perDay: '$8,976.27' },
  { month: 'Jan', perDay: '$9,299.90' },
  { month: 'Feb', perDay: '$8,993.64' },
  { month: 'Mar', perDay: '$9,043.00' },
]

export const PDF_COPY_METRICS_PRODUCTION_PERFORMANCE: {
  id: string
  label: string
}[] = [
  { id: 'ppSummary', label: 'Summary' },
  { id: 'ppProduction', label: 'Production (Monthly)' },
  { id: 'ppProductionGoal', label: 'Production Goal (Monthly)' },
  { id: 'ppPerHour', label: 'Average Production per Hour' },
  { id: 'ppPerDay', label: 'Average Production per Day' },
]

export const ALL_METRICS_PRODUCTION_PERFORMANCE_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_PRODUCTION_PERFORMANCE.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_CFE_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Collections', value: '$786,912.00' },
  { metric: 'Production', value: '$831,437.00' },
  { metric: 'Net Collection Percentage', value: '94.6%' },
  { metric: 'Same-day Payments', value: '$286,046.00' },
]

export const PREVIEW_CFE_COLLECTIONS_ROWS: {
  month: string
  collections: string
}[] = [
  { month: 'Dec', collections: '$222,672.00' },
  { month: 'Jan', collections: '$281,322.00' },
  { month: 'Feb', collections: '$240,369.00' },
  { month: 'Mar', collections: '$42,549.00' },
]

export const PREVIEW_CFE_NET_COLLECTION_ROWS: {
  month: string
  netCollection: string
}[] = [
  { month: 'Dec', netCollection: '96.24%' },
  { month: 'Jan', netCollection: '99.76%' },
  { month: 'Feb', netCollection: '95.17%' },
  { month: 'Mar', netCollection: '98.89%' },
]

export const PREVIEW_CFE_SAME_DAY_ROWS: {
  month: string
  sameDay: string
}[] = [
  { month: 'Dec', sameDay: '$85,456.00' },
  { month: 'Jan', sameDay: '$97,629.00' },
  { month: 'Feb', sameDay: '$86,179.00' },
  { month: 'Mar', sameDay: '$16,782.00' },
]

export const PREVIEW_CFE_PRODUCTION_REFERENCE_ROWS: {
  month: string
  production: string
}[] = [
  { month: 'Dec', production: '$237,590.00' },
  { month: 'Jan', production: '$287,686.00' },
  { month: 'Feb', production: '$259,648.00' },
  { month: 'Mar', production: '$46,513.00' },
]

export const PDF_COPY_METRICS_COLLECTIONS_FINANCIAL_EFFICIENCY: {
  id: string
  label: string
}[] = [
  { id: 'cfeSummary', label: 'Summary' },
  { id: 'cfeCollections', label: 'Collections per Provider (Monthly)' },
  { id: 'cfeNetCollection', label: 'Net Collection Percentage (Monthly)' },
  { id: 'cfeSameDay', label: 'Same-day Payments (Monthly)' },
  { id: 'cfeProductionRef', label: 'Production Reference (Monthly)' },
]

export const ALL_METRICS_COLLECTIONS_FINANCIAL_EFFICIENCY_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_COLLECTIONS_FINANCIAL_EFFICIENCY.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_CMT_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Total Procedures', value: '2,407' },
  { metric: 'Crown Percentage', value: '23.2%' },
  { metric: 'SRP Percentage', value: '15.5%' },
  { metric: 'High-value Procedure Percentage', value: '18.6%' },
]

export const PREVIEW_CMT_CROWN_ROWS: {
  month: string
  crownCount: string
}[] = [
  { month: 'Dec', crownCount: '162' },
  { month: 'Jan', crownCount: '197' },
  { month: 'Feb', crownCount: '177' },
  { month: 'Mar', crownCount: '22' },
]

export const PREVIEW_CMT_SRP_ROWS: {
  month: string
  srpCount: string
}[] = [
  { month: 'Dec', srpCount: '103' },
  { month: 'Jan', srpCount: '125' },
  { month: 'Feb', srpCount: '125' },
  { month: 'Mar', srpCount: '19' },
]

export const PREVIEW_CMT_FILLING_ROWS: {
  month: string
  fillingCount: string
}[] = [
  { month: 'Dec', fillingCount: '256' },
  { month: 'Jan', fillingCount: '326' },
  { month: 'Feb', fillingCount: '313' },
  { month: 'Mar', fillingCount: '46' },
]

export const PREVIEW_CMT_EXTRACTION_ROWS: {
  month: string
  extractionCount: string
}[] = [
  { month: 'Dec', extractionCount: '64' },
  { month: 'Jan', extractionCount: '103' },
  { month: 'Feb', extractionCount: '81' },
  { month: 'Mar', extractionCount: '22' },
]

export const PDF_COPY_METRICS_CLINICAL_MIX_TREATMENT_PROFILE: {
  id: string
  label: string
}[] = [
  { id: 'cmtSummary', label: 'Summary' },
  { id: 'cmtCrown', label: 'Crown Procedures (Monthly)' },
  { id: 'cmtSrp', label: 'SRP Procedures (Monthly)' },
  { id: 'cmtFilling', label: 'Filling Procedures (Monthly)' },
  { id: 'cmtExtraction', label: 'Extraction Procedures (Monthly)' },
]

export const ALL_METRICS_CLINICAL_MIX_TREATMENT_PROFILE_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_CLINICAL_MIX_TREATMENT_PROFILE.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_TE_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Treatment Acceptance Rate', value: '63.6%' },
  { metric: 'Completed vs Diagnosed Rate', value: '54.0%' },
  { metric: 'Total Diagnosed Cases', value: '1,649' },
  { metric: 'Unscheduled Treatments', value: '627' },
]

export const PREVIEW_TE_STATUS_DISTRIBUTION_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Accepted', value: '40.9%' },
  { metric: 'Completed', value: '34.7%' },
  { metric: 'Unscheduled', value: '24.4%' },
]

export const PREVIEW_TE_ACCEPTANCE_RATE_ROWS: {
  month: string
  acceptanceRate: string
}[] = [
  { month: 'Dec', acceptanceRate: '63.97%' },
  { month: 'Jan', acceptanceRate: '71.41%' },
  { month: 'Feb', acceptanceRate: '69.44%' },
  { month: 'Mar', acceptanceRate: '67.09%' },
]

export const PREVIEW_TE_COMPLETED_VS_DIAGNOSED_ROWS: {
  month: string
  completionRate: string
}[] = [
  { month: 'Dec', completionRate: '59.58%' },
  { month: 'Jan', completionRate: '56.39%' },
  { month: 'Feb', completionRate: '60.02%' },
  { month: 'Mar', completionRate: '57.54%' },
]

export const PREVIEW_TE_DIAGNOSED_VOLUME_ROWS: {
  month: string
  diagnosedCases: string
}[] = [
  { month: 'Dec', diagnosedCases: '476' },
  { month: 'Jan', diagnosedCases: '596' },
  { month: 'Feb', diagnosedCases: '492' },
  { month: 'Mar', diagnosedCases: '85' },
]

export const PREVIEW_TE_UNSCHEDULED_ROWS: {
  month: string
  unscheduledCount: string
}[] = [
  { month: 'Dec', unscheduledCount: '168' },
  { month: 'Jan', unscheduledCount: '250' },
  { month: 'Feb', unscheduledCount: '173' },
  { month: 'Mar', unscheduledCount: '36' },
]

export const PDF_COPY_METRICS_TREATMENT_EFFECTIVENESS: {
  id: string
  label: string
}[] = [
  { id: 'teSummary', label: 'Summary' },
  { id: 'teStatusDistribution', label: 'Treatment Status Distribution' },
  { id: 'teAcceptance', label: 'Treatment Acceptance Rate (Monthly)' },
  { id: 'teCompleted', label: 'Completed vs Diagnosed (Monthly)' },
  { id: 'teDiagnosed', label: 'Diagnosed Treatment Volume (Monthly)' },
  { id: 'teUnscheduled', label: 'Unscheduled Treatment (Monthly)' },
]

export const ALL_METRICS_TREATMENT_EFFECTIVENESS_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_TREATMENT_EFFECTIVENESS.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_ES_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Efficiency Score', value: '$1,151/hr' },
  { metric: 'Production per Hour', value: '$1,365/hr' },
  { metric: 'Chair Utilization', value: '84.3%' },
  { metric: 'Total Production', value: '$879,725' },
]

export const PREVIEW_ES_EFFICIENCY_SCORE_ROWS: {
  month: string
  efficiencyScore: string
}[] = [
  { month: 'Dec', efficiencyScore: '$1,175.00' },
  { month: 'Jan', efficiencyScore: '$1,156.00' },
  { month: 'Feb', efficiencyScore: '$1,245.00' },
  { month: 'Mar', efficiencyScore: '$1,126.00' },
]

export const PREVIEW_ES_PRODUCTION_PER_HOUR_ROWS: {
  month: string
  productionPerHour: string
}[] = [
  { month: 'Dec', productionPerHour: '$1,396.00' },
  { month: 'Jan', productionPerHour: '$1,391.00' },
  { month: 'Feb', productionPerHour: '$1,468.00' },
  { month: 'Mar', productionPerHour: '$1,343.00' },
]

export const PREVIEW_ES_CHAIR_UTILIZATION_ROWS: {
  month: string
  chairUtilization: string
}[] = [
  { month: 'Dec', chairUtilization: '88.01%' },
  { month: 'Jan', chairUtilization: '81.56%' },
  { month: 'Feb', chairUtilization: '84.86%' },
  { month: 'Mar', chairUtilization: '86.24%' },
]

export const PREVIEW_ES_PROVIDER_COMPARISON_ROWS: {
  provider: string
  production: string
  productionPerHour: string
  efficiencyScore: string
  chairUtilization: string
}[] = [
  {
    provider: 'Dr. Smith',
    production: '$387,752.00',
    productionPerHour: '$930.00',
    efficiencyScore: '$793.00',
    chairUtilization: '85.3%',
  },
  {
    provider: 'Dr. Lee',
    production: '$199,533.00',
    productionPerHour: '$622.00',
    efficiencyScore: '$449.00',
    chairUtilization: '72.3%',
  },
  {
    provider: 'Dr. Patel',
    production: '$316,978.00',
    productionPerHour: '$921.00',
    efficiencyScore: '$709.00',
    chairUtilization: '77.0%',
  },
  {
    provider: 'Dr. Garcia',
    production: '$380,375.00',
    productionPerHour: '$899.00',
    efficiencyScore: '$712.00',
    chairUtilization: '79.2%',
  },
  {
    provider: 'Dr. Chen',
    production: '$388,796.00',
    productionPerHour: '$1,029.00',
    efficiencyScore: '$946.00',
    chairUtilization: '92.0%',
  },
  {
    provider: 'Dr. Williams',
    production: '$216,188.00',
    productionPerHour: '$607.00',
    efficiencyScore: '$446.00',
    chairUtilization: '73.4%',
  },
  {
    provider: 'Dr. Kim',
    production: '$191,131.00',
    productionPerHour: '$528.00',
    efficiencyScore: '$494.00',
    chairUtilization: '93.5%',
  },
]

export const PDF_COPY_METRICS_EFFICIENCY_SCORECARD: {
  id: string
  label: string
}[] = [
  { id: 'esSummary', label: 'Summary' },
  { id: 'esEfficiency', label: 'Efficiency Score (Monthly)' },
  { id: 'esProductionPerHour', label: 'Production per Hour (Monthly)' },
  { id: 'esChairUtilization', label: 'Chair Utilization (Monthly)' },
  { id: 'esProviderComparison', label: 'Provider Financial Comparison' },
]

export const ALL_METRICS_EFFICIENCY_SCORECARD_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_EFFICIENCY_SCORECARD.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_RRA_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Total Production', value: '$958,131.00' },
  { metric: 'Total Collections', value: '$872,608.00' },
  { metric: 'Net Collection %', value: '91.1%' },
  { metric: 'Gross Collection %', value: '84.8%' },
  { metric: 'Same-Day Collections', value: '$284,834.00' },
  { metric: 'Adjustment Rate %', value: '7.4%' },
]

export const PREVIEW_RRA_PRODUCTION_VS_COLLECTIONS_ROWS: {
  month: string
  production: string
  collections: string
}[] = [
  { month: 'Dec', production: '$280,381.00', collections: '$248,250.00' },
  { month: 'Jan', production: '$334,312.00', collections: '$306,832.00' },
  { month: 'Feb', production: '$297,353.00', collections: '$266,961.00' },
  { month: 'Mar', production: '$46,084.00', collections: '$50,565.00' },
]

export const PREVIEW_RRA_NET_COLLECTION_ROWS: {
  month: string
  netCollection: string
}[] = [
  { month: 'Dec', netCollection: '88.54%' },
  { month: 'Jan', netCollection: '91.78%' },
  { month: 'Feb', netCollection: '89.78%' },
  { month: 'Mar', netCollection: '109.72%' },
]

export const PREVIEW_RRA_ADJUSTMENT_RATE_ROWS: {
  month: string
  adjustmentRate: string
}[] = [
  { month: 'Dec', adjustmentRate: '7.40%' },
  { month: 'Jan', adjustmentRate: '7.43%' },
  { month: 'Feb', adjustmentRate: '7.28%' },
  { month: 'Mar', adjustmentRate: '8.57%' },
]

export const PREVIEW_RRA_INSURANCE_VS_PATIENT_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Insurance', value: '54.8%' },
  { metric: 'Patient', value: '45.2%' },
]

export const PDF_COPY_METRICS_REVENUE_REALIZATION_ANALYTICS: {
  id: string
  label: string
}[] = [
  { id: 'rraSummary', label: 'Summary' },
  { id: 'rraProdVsColl', label: 'Production vs Collections (Monthly)' },
  { id: 'rraNetCollection', label: 'Net Collection % (Monthly)' },
  { id: 'rraAdjustmentRate', label: 'Adjustment Rate % (Monthly)' },
  { id: 'rraInsuranceVsPatient', label: 'Insurance vs Patient Payments' },
]

export const ALL_METRICS_REVENUE_REALIZATION_ANALYTICS_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_REVENUE_REALIZATION_ANALYTICS.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_RRISK_SUMMARY_ROWS: { metric: string; value: string }[] = [
  { metric: 'Total Accounts Receivable', value: '$415,721.00' },
  { metric: 'Insurance AR', value: '$244,717.00' },
  { metric: 'Patient AR', value: '$164,175.00' },
  { metric: 'AR >90 Days %', value: '17.1%' },
  { metric: 'Average Days in AR', value: '39.2' },
]

export const PREVIEW_RRISK_AGING_ROWS: {
  bucket: string
  amount: string
}[] = [
  { bucket: '0-30 days', amount: '$160,000.00' },
  { bucket: '31-60 days', amount: '$110,000.00' },
  { bucket: '61-90 days', amount: '$78,000.00' },
  { bucket: '90+ days', amount: '$72,000.00' },
]

export const PREVIEW_RRISK_AR_TREND_ROWS: {
  month: string
  averageAr: string
}[] = [
  { month: 'Dec', averageAr: '$418,899.00' },
  { month: 'Jan', averageAr: '$419,306.00' },
  { month: 'Feb', averageAr: '$412,579.00' },
  { month: 'Mar', averageAr: '$398,606.00' },
]

export const PREVIEW_RRISK_AVG_DAYS_ROWS: {
  month: string
  avgDays: string
}[] = [
  { month: 'Dec', avgDays: '38.1%' },
  { month: 'Jan', avgDays: '39.8%' },
  { month: 'Feb', avgDays: '39.8%' },
  { month: 'Mar', avgDays: '38.0%' },
]

export const PREVIEW_RRISK_HIGH_BALANCE_ROWS: {
  patient: string
  balance: string
  daysOutstanding: string
}[] = [
  { patient: 'John Smith', balance: '$8,200.00', daysOutstanding: '95 days' },
  {
    patient: 'Maria Garcia',
    balance: '$7,600.00',
    daysOutstanding: '103 days',
  },
  { patient: 'Daniel Lee', balance: '$6,800.00', daysOutstanding: '88 days' },
  { patient: 'Anna Patel', balance: '$6,400.00', daysOutstanding: '120 days' },
  { patient: 'Kevin Chen', balance: '$5,900.00', daysOutstanding: '94 days' },
]

export const PDF_COPY_METRICS_RECEIVABLES_RISK_ANALYSIS: {
  id: string
  label: string
}[] = [
  { id: 'rriskSummary', label: 'Summary' },
  { id: 'rriskAging', label: 'AR Aging Buckets' },
  { id: 'rriskArTrend', label: 'AR Trend' },
  { id: 'rriskAvgDays', label: 'Average Days in AR' },
  { id: 'rriskHighBalance', label: 'High Balance Patients' },
]

export const ALL_METRICS_RECEIVABLES_RISK_ANALYSIS_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_RECEIVABLES_RISK_ANALYSIS.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)

export const PREVIEW_CLAIMS_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Total Claims Submitted', value: '7,809' },
  { metric: 'Claims Pending', value: '39' },
  { metric: 'Claims Overdue', value: '11' },
  { metric: 'Claims Denied / Rejected', value: '7' },
  { metric: 'Claim Acceptance Rate %', value: '99.9%' },
  { metric: 'Resubmission Rate %', value: '0.1%' },
]

export const PREVIEW_CLAIMS_PAYMENT_TREND_ROWS: {
  month: string
  avgDays: string
}[] = [
  { month: 'Dec', avgDays: '16.9' },
  { month: 'Jan', avgDays: '18.7' },
  { month: 'Feb', avgDays: '19.2' },
  { month: 'Mar', avgDays: '19.3' },
]

export const PREVIEW_CLAIMS_STATUS_ROWS: {
  status: string
  count: string
}[] = [
  { status: 'Submitted', count: '7,809' },
  { status: 'Pending', count: '39' },
  { status: 'Overdue', count: '11' },
  { status: 'Denied / Rejected', count: '7' },
]

export const PREVIEW_CLAIMS_CARRIER_ROWS: {
  carrier: string
  avgDays: string
}[] = [
  { carrier: 'Delta Dental', avgDays: '23.1' },
  { carrier: 'Aetna', avgDays: '22.8' },
  { carrier: 'MetLife', avgDays: '19.3' },
  { carrier: 'Cigna', avgDays: '16.3' },
  { carrier: 'Guardian', avgDays: '24.3' },
]

export const PREVIEW_CLAIMS_AGING_ROWS: {
  bucket: string
  amount: string
}[] = [
  { bucket: '0-30', amount: '$140,000.00' },
  { bucket: '31-60', amount: '$82,000.00' },
  { bucket: '61-90', amount: '$54,000.00' },
  { bucket: '90+', amount: '$46,000.00' },
]

export const PDF_COPY_METRICS_CLAIMS_PERFORMANCE: {
  id: string
  label: string
}[] = [
  { id: 'claimsSummary', label: 'Summary' },
  { id: 'claimsPaymentTrend', label: 'Average Days to Payment Trend' },
  { id: 'claimsStatus', label: 'Claims Status Overview' },
  { id: 'claimsCarrier', label: 'Average Days to Payment by Carrier' },
  { id: 'claimsAging', label: 'Insurance Aging Breakdown' },
]

export const ALL_METRICS_CLAIMS_PERFORMANCE_SELECTED: Record<string, boolean> =
  PDF_COPY_METRICS_CLAIMS_PERFORMANCE.reduce(
    (acc, { id }) => {
      acc[id] = true
      return acc
    },
    {} as Record<string, boolean>,
  )

export const PREVIEW_GAV_SUMMARY_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Production Goal', value: '$1.25M' },
  { metric: 'Production Actual', value: '$1.24M' },
  { metric: 'Collections', value: '$1.08M' },
  { metric: 'Variance (Production %)', value: '-0.9%' },
]

export const PREVIEW_GAV_YTD_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'YTD Production Goal', value: '$8.90M' },
  { metric: 'YTD Production Actual', value: '$8.74M' },
  { metric: 'YTD Progress', value: '98.3%' },
  { metric: 'MTD Production', value: '$820K' },
  { metric: 'Forecast EOM Production', value: '$1.29M' },
]

export const PREVIEW_GAV_RUN_RATE_ROWS: {
  metric: string
  value: string
}[] = [
  { metric: 'Run Rate (MTD)', value: '$59K' },
  { metric: 'Required Pace', value: '$57K' },
  { metric: 'Gap (Run - Required)', value: '$2K' },
]

export const PREVIEW_GAV_VARIANCE_TREND_ROWS: {
  month: string
  variance: string
}[] = [
  { month: 'Apr', variance: '-4.7%' },
  { month: 'May', variance: '-0.7%' },
  { month: 'Jun', variance: '-3.9%' },
  { month: 'Jul', variance: '-1.5%' },
  { month: 'Aug', variance: '2.0%' },
  { month: 'Sep', variance: '-0.9%' },
]

export const PREVIEW_GAV_PRODUCTION_ROWS: {
  month: string
  goal: string
  actual: string
  variance: string
}[] = [
  { month: 'Apr', goal: '$1.18M', actual: '$1.12M', variance: '-4.7%' },
  { month: 'May', goal: '$1.20M', actual: '$1.19M', variance: '-0.7%' },
  { month: 'Jun', goal: '$1.22M', actual: '$1.17M', variance: '-3.9%' },
  { month: 'Jul', goal: '$1.23M', actual: '$1.21M', variance: '-1.5%' },
  { month: 'Aug', goal: '$1.24M', actual: '$1.26M', variance: '2.0%' },
  { month: 'Sep', goal: '$1.25M', actual: '$1.24M', variance: '-0.9%' },
]

export const PREVIEW_GAV_COLLECTION_ROWS: {
  month: string
  goal: string
  actual: string
  variance: string
}[] = [
  { month: 'Apr', goal: '$1.02M', actual: '$980K', variance: '-3.9%' },
  { month: 'May', goal: '$1.04M', actual: '$1.03M', variance: '-0.8%' },
  { month: 'Jun', goal: '$1.05M', actual: '$1.01M', variance: '-3.3%' },
  { month: 'Jul', goal: '$1.06M', actual: '$1.05M', variance: '-1.6%' },
  { month: 'Aug', goal: '$1.08M', actual: '$1.10M', variance: '2.0%' },
  { month: 'Sep', goal: '$1.09M', actual: '$1.08M', variance: '-1.0%' },
]

export const PREVIEW_GAV_LOCATION_ROWS: {
  location: string
  goal: string
  actual: string
  variance: string
}[] = [
  { location: 'Downtown', goal: '$520K', actual: '$498K', variance: '-4.2%' },
  { location: 'Uptown', goal: '$410K', actual: '$435K', variance: '6.1%' },
  { location: 'Westside', goal: '$320K', actual: '$306K', variance: '-4.4%' },
]

export const PREVIEW_GAV_PROVIDER_ROWS: {
  provider: string
  goal: string
  actual: string
  variance: string
}[] = [
  { provider: 'Dr. Smith', goal: '$240K', actual: '$232K', variance: '-3.3%' },
  { provider: 'Dr. Patel', goal: '$210K', actual: '$198K', variance: '-5.7%' },
  { provider: 'Dr. Snow', goal: '$185K', actual: '$194K', variance: '4.6%' },
  { provider: 'Dr. Garcia', goal: '$175K', actual: '$168K', variance: '-4.0%' },
  { provider: 'Dr. Chen', goal: '$160K', actual: '$171K', variance: '6.9%' },
  { provider: 'Dr. Rivera', goal: '$140K', actual: '$130K', variance: '-7.5%' },
]

export const PDF_COPY_METRICS_GOAL_ATTAINMENT_VARIANCE: {
  id: string
  label: string
}[] = [
  { id: 'gavSummary', label: 'Summary' },
  { id: 'gavYtd', label: 'YTD Progress & Forecast' },
  {
    id: 'gavRunRate',
    label: 'Daily Run Rate vs Required Pace (Current Month)',
  },
  { id: 'gavVarianceTrend', label: 'Variance % Trend (Monthly)' },
  { id: 'gavProduction', label: 'Production Goal vs Actual (Monthly)' },
  { id: 'gavCollection', label: 'Collection Goal vs Actual (Monthly)' },
  { id: 'gavLocation', label: 'Location Goal Comparison (Current Month)' },
  { id: 'gavProvider', label: 'Provider Goal Tracking (Current Month)' },
]

export const ALL_METRICS_GOAL_ATTAINMENT_VARIANCE_SELECTED: Record<
  string,
  boolean
> = PDF_COPY_METRICS_GOAL_ATTAINMENT_VARIANCE.reduce(
  (acc, { id }) => {
    acc[id] = true
    return acc
  },
  {} as Record<string, boolean>,
)
